/**
 * Status page content — 100% fidelity to content/company/status.md
 * Cross-reference: content/company/status.md
 * 18 sections, all handcrafted from source.
 */

export const statusMeta = {
  title: "Status | Perception X2 Platform Status",
  description:
    "Real-time platform status for Perception X2. 99.9999% uptime. Live system health, incident reports, and performance metrics across all services.",
  slug: "status",
  canonical: "https://perception.ac/status",
};

export interface HeroData {
  eyebrow: string;
  title: string;
  titleAccent: string;
  lede: string;
  body: string;
  cta: { label: string; href: string }[];
  trustIndicators: string[];
  pillars?: { title: string; body: string }[];
  intro?: string;
}

export const hero: HeroData = {
  eyebrow: "Platform Status",
  title: "Platform Status.",
  titleAccent: "Real-Time Visibility.",
  lede: "Perception X2 operates at 99.9999% uptime — 31.5 seconds maximum downtime per year. This status page provides real-time transparency into our platform health, incident history, and performance metrics.",
  body: "Every service, every region, every metric — visible, accountable, verifiable. We believe operational transparency is foundational to trust. This page reflects the live state of all Perception X2 systems.",
  cta: [
    { label: "View System Status", href: "#current-status" },
    { label: "Subscribe to Updates", href: "#communication-channels" },
  ],
  trustIndicators: [
    "99.9999% Uptime SLA",
    "24/7/365 Monitoring",
    "Zero Security Incidents",
    "Real-Time Incident Reporting",
  ],
};

export interface ExecSummaryData {
  eyebrow: string;
  title: string;
  intro: string;
  pillars: { title: string; body: string }[];
  cta: { label: string; href: string }[];
}

export const executiveSummary: ExecSummaryData = {
  eyebrow: "Executive Summary",
  title: "Operational Excellence, Transparently Reported",
  intro: "Perception X2 is built for organizations where downtime is not an option. Our infrastructure spans multiple continents, with redundant systems, automatic failover, and continuous monitoring ensuring consistent availability across all services.",
  pillars: [
    { title: "Platform Health at a Glance", body: "All core services are currently operational. Our monitoring infrastructure tracks over 2,000 health endpoints across all regions, providing real-time visibility into system performance. When anomalies are detected, automated responses activate within milliseconds — before impact reaches clients." },
    { title: "Uptime Commitment", body: "Our 99.9999% uptime SLA represents the highest standard in the industry. This translates to approximately 31.5 seconds of allowable downtime per year. Every service page on this status dashboard reflects real-time data from our monitoring infrastructure." },
    { title: "Incident Transparency", body: "When incidents occur, we communicate immediately. This status page provides honest, detailed reporting on any service disruption — including root cause analysis, impact assessment, and remediation steps. No obfuscation. No delay." },
  ],
  cta: [
    { label: "View Current Status", href: "#current-status" },
    { label: "Review Incident History", href: "#incident-history" },
  ],
};

export interface CurrentStatusData {
  eyebrow: string;
  title: string;
  intro: string;
  overall: string;
  coreServices: { service: string; status: string; latency: string; uptime: string }[];
  aiEngineServices: { service: string; status: string; latency: string; uptime: string }[];
  infraServices: { service: string; status: string; latency: string; uptime: string }[];
  lastUpdated: string;
  pillars?: { title: string; body: string }[];
}

export const currentStatus: CurrentStatusData = {
  eyebrow: "Current Status",
  title: "Live System Health",
  intro: "All Perception X2 services are currently operational.",
  overall: "All systems operational",
  coreServices: [
    { service: "Perception X2 Main Platform", status: "Operational", latency: "42ms", uptime: "99.9999%" },
    { service: "API Gateway", status: "Operational", latency: "18ms", uptime: "99.9999%" },
    { service: "Authentication Services", status: "Operational", latency: "23ms", uptime: "99.9999%" },
    { service: "Data Processing Pipeline", status: "Operational", latency: "67ms", uptime: "99.9999%" },
    { service: "Real-Time Analytics", status: "Operational", latency: "31ms", uptime: "99.9999%" },
  ],
  aiEngineServices: [
    { service: "Organic Simulation Engine", status: "Operational", latency: "89ms", uptime: "99.9999%" },
    { service: "Narrative Generation Engine", status: "Operational", latency: "54ms", uptime: "99.9999%" },
    { service: "Cognitive Resonance Engine", status: "Operational", latency: "41ms", uptime: "99.9999%" },
    { service: "Echo Chamber Architecture", status: "Operational", latency: "72ms", uptime: "99.9999%" },
    { service: "Autonomous Operations Engine", status: "Operational", latency: "63ms", uptime: "99.9999%" },
  ],
  infraServices: [
    { service: "Content Delivery Network", status: "Operational", latency: "12ms", uptime: "99.9999%" },
    { service: "Database Cluster", status: "Operational", latency: "8ms", uptime: "99.9999%" },
    { service: "Object Storage", status: "Operational", latency: "15ms", uptime: "99.9999%" },
    { service: "Message Queue", status: "Operational", latency: "5ms", uptime: "99.9999%" },
    { service: "Cache Layer", status: "Operational", latency: "3ms", uptime: "99.9999%" },
  ],
  lastUpdated: "Last updated: Real-time · Auto-refresh every 30 seconds",
};

export interface SystemHealthData {
  eyebrow: string;
  title: string;
  intro: string;
  tiers: { name: string; description: string }[];
  regions: { region: string; avg: string; p99: string; status: string }[];
  dependencies: { dependency: string; status: string; impact: string }[];
  refresh: string;
  pillars?: { title: string; body: string }[];
}

export const systemHealth: SystemHealthData = {
  eyebrow: "System Health Overview",
  title: "Comprehensive Health Monitoring",
  intro: "Perception X2 monitors over 2,000 health endpoints across all services, regions, and dependencies. Our automated systems detect and respond to anomalies in milliseconds.",
  tiers: [
    { name: "Tier 1: Synthetic Monitoring", description: "Automated health checks every 10 seconds across all endpoints" },
    { name: "Tier 2: Real User Monitoring", description: "Performance tracking from actual client interactions" },
    { name: "Tier 3: Infrastructure Monitoring", description: "CPU, memory, disk, and network metrics across all nodes" },
  ],
  regions: [
    { region: "North America", avg: "23ms", p99: "45ms", status: "Operational" },
    { region: "Europe", avg: "31ms", p99: "58ms", status: "Operational" },
    { region: "Asia-Pacific", avg: "42ms", p99: "79ms", status: "Operational" },
  ],
  dependencies: [
    { dependency: "DNS Providers", status: "Operational", impact: "Platform inaccessible" },
    { dependency: "SSL Certificates", status: "Valid", impact: "Security warnings" },
    { dependency: "Third-Party APIs", status: "Operational", impact: "Reduced functionality" },
    { dependency: "CDN Edge Nodes", status: "Operational", impact: "Slower content delivery" },
  ],
  refresh: "Health data refreshed every 10 seconds",
};

export interface UptimeData {
  eyebrow: string;
  title: string;
  intro: string;
  monthly: { month: string; uptime: string; downtime: string; incidents: string }[];
  annual: { year: string; uptime: string; total: string; sla: string }[];
  calculation: string;
  audit: string;
  pillars?: { title: string; body: string }[];
}

export const uptimeMetrics: UptimeData = {
  eyebrow: "Uptime Metrics",
  title: "99.9999% Uptime. Documented.",
  intro: "Our uptime commitment is not aspirational — it is operational. Every metric on this page reflects verified, auditable data from our monitoring infrastructure.",
  monthly: [
    { month: "June 2026", uptime: "100.0000%", downtime: "0s", incidents: "0" },
    { month: "May 2026", uptime: "99.9999%", downtime: "2.6s", incidents: "0" },
    { month: "April 2026", uptime: "100.0000%", downtime: "0s", incidents: "0" },
    { month: "March 2026", uptime: "99.9999%", downtime: "3.1s", incidents: "0" },
    { month: "February 2026", uptime: "100.0000%", downtime: "0s", incidents: "0" },
    { month: "January 2026", uptime: "99.9999%", downtime: "2.8s", incidents: "0" },
  ],
  annual: [
    { year: "2026 (YTD)", uptime: "99.9999%", total: "8.5s", sla: "Yes" },
    { year: "2025", uptime: "99.9999%", total: "31.2s", sla: "Yes" },
    { year: "2024", uptime: "99.9999%", total: "28.7s", sla: "Yes" },
  ],
  calculation: "Uptime Percentage = (Total Minutes - Downtime Minutes) / Total Minutes × 100\n99.9999% = 525,599.9 minutes available / 525,600 minutes total\nAllowable downtime: 31.5 seconds per year",
  audit: "Uptime data audited quarterly by independent monitoring services",
};

export interface PerformanceData {
  eyebrow: string;
  title: string;
  intro: string;
  core: { metric: string; current: string; target: string; status: string }[];
  throughput: { metric: string; current: string; capacity: string; utilization: string }[];
  resources: { resource: string; current: string; threshold: string; status: string }[];
  refresh: string;
  pillars?: { title: string; body: string }[];
}

export const performanceMetrics: PerformanceData = {
  eyebrow: "Performance Metrics",
  title: "Platform Performance Benchmarks",
  intro: "Performance is measured continuously across all services. These metrics reflect real-time platform health and are updated every 60 seconds.",
  core: [
    { metric: "API Response Time", current: "23ms", target: "< 100ms", status: "Exceeds" },
    { metric: "Database Query Time", current: "8ms", target: "< 50ms", status: "Exceeds" },
    { metric: "Content Delivery", current: "12ms", target: "< 30ms", status: "Exceeds" },
    { metric: "Authentication Latency", current: "23ms", target: "< 50ms", status: "Exceeds" },
    { metric: "AI Processing Speed", current: "54ms", target: "< 200ms", status: "Exceeds" },
  ],
  throughput: [
    { metric: "Requests per Second", current: "45,000", capacity: "200,000", utilization: "22.5%" },
    { metric: "Concurrent Users", current: "12,500", capacity: "100,000", utilization: "12.5%" },
    { metric: "Data Processing", current: "2.1 TB/hr", capacity: "10 TB/hr", utilization: "21%" },
    { metric: "API Calls", current: "1.2M/min", capacity: "5M/min", utilization: "24%" },
  ],
  resources: [
    { resource: "CPU Usage", current: "34%", threshold: "80%", status: "Healthy" },
    { resource: "Memory Usage", current: "42%", threshold: "85%", status: "Healthy" },
    { resource: "Disk Usage", current: "28%", threshold: "75%", status: "Healthy" },
    { resource: "Network Bandwidth", current: "18%", threshold: "70%", status: "Healthy" },
  ],
  refresh: "Performance metrics updated every 60 seconds",
};

export interface IncidentsData {
  eyebrow: string;
  title: string;
  intro: string;
  recent: string;
  past: { date: string; severity: string; duration: string; impact: string; resolution: string }[];
  severity: { severity: string; definition: string; response: string; communication: string }[];
  reports: { title: string; body: string };
  cta: { label: string; href: string }[];
  pillars?: { title: string; body: string }[];
}

export const incidentHistory: IncidentsData = {
  eyebrow: "Incident History",
  title: "Transparent Incident Reporting",
  intro: "When incidents occur, we communicate with transparency and precision. Every incident on this page includes root cause analysis, impact assessment, and remediation steps.",
  recent: "No incidents reported in the past 90 days.",
  past: [
    { date: "2025-11-14", severity: "Minor", duration: "2.6 seconds", impact: "API latency spike", resolution: "Automatic failover" },
    { date: "2025-08-22", severity: "Minor", duration: "3.1 seconds", impact: "Database connection timeout", resolution: "Cluster rebalancing" },
    { date: "2025-03-08", severity: "Minor", duration: "2.8 seconds", impact: "CDN edge node degradation", resolution: "Edge node replacement" },
  ],
  severity: [
    { severity: "Critical", definition: "Complete service outage", response: "Immediate", communication: "Real-time updates every 5 minutes" },
    { severity: "Major", definition: "Significant feature degradation", response: "< 5 minutes", communication: "Updates every 15 minutes" },
    { severity: "Minor", definition: "Limited impact, workarounds available", response: "< 15 minutes", communication: "Updates every 30 minutes" },
    { severity: "Informational", definition: "No client impact", response: "< 1 hour", communication: "Post-incident report" },
  ],
  reports: {
    title: "Post-Incident Reports",
    body: "All incidents with severity Major or Critical receive a detailed post-incident report within 48 hours. Reports include root cause analysis, timeline, impact assessment, and preventive measures.",
  },
  cta: [
    { label: "Request Post-Incident Report", href: "#communication-channels" },
    { label: "View Full Incident History", href: "#incident-history" },
  ],
};

export interface MaintenanceData {
  eyebrow: string;
  title: string;
  intro: string;
  upcoming: string;
  recent: { date: string; duration: string; services: string; impact: string }[];
  windows: { window: string; time: string; frequency: string }[];
  communication: string[];
  cta: { label: string; href: string }[];
  pillars?: { title: string; body: string }[];
}

export const scheduledMaintenance: MaintenanceData = {
  eyebrow: "Scheduled Maintenance",
  title: "Planned System Updates",
  intro: "Scheduled maintenance windows are communicated at least 72 hours in advance. All maintenance is performed during low-traffic periods to minimize impact.",
  upcoming: "No scheduled maintenance at this time.",
  recent: [
    { date: "2026-06-08", duration: "12 minutes", services: "Database Cluster", impact: "Brief latency increase" },
    { date: "2026-05-25", duration: "8 minutes", services: "API Gateway", impact: "No client impact" },
    { date: "2026-05-11", duration: "15 minutes", services: "AI Engine Services", impact: "No client impact" },
  ],
  windows: [
    { window: "Standard Maintenance", time: "02:00 - 04:00", frequency: "As needed" },
    { window: "Emergency Maintenance", time: "Immediate", frequency: "Critical only" },
    { window: "Infrastructure Upgrade", time: "01:00 - 03:00", frequency: "Monthly" },
  ],
  communication: [
    "Email notifications sent 72 hours, 24 hours, and 1 hour before maintenance",
    "Status page banner displayed during maintenance window",
    "Real-time updates throughout maintenance period",
    "Completion notification sent upon successful deployment",
  ],
  cta: [{ label: "Subscribe to Maintenance Notifications", href: "#communication-channels" }],
};

export interface SlaData {
  eyebrow: string;
  title: string;
  intro: string;
  tiers: { tier: string; uptime: string; response: string; support: string }[];
  metrics: { metric: string; enterprise: string; professional: string; standard: string }[];
  monitoring: string[];
  guarantees: string[];
  cta: { label: string; href: string }[];
  pillars?: { title: string; body: string }[];
}

export const serviceLevelAgreements: SlaData = {
  eyebrow: "Service Level Agreements",
  title: "Our Commitment to Excellence",
  intro: "Perception X2 maintains the highest service level agreements in the industry. Our SLAs are backed by measurable metrics and independent verification.",
  tiers: [
    { tier: "Enterprise", uptime: "99.9999%", response: "< 1 hour", support: "24/7 dedicated" },
    { tier: "Professional", uptime: "99.999%", response: "< 4 hours", support: "24/7 shared" },
    { tier: "Standard", uptime: "99.99%", response: "< 8 hours", support: "Business hours" },
  ],
  metrics: [
    { metric: "Uptime", enterprise: "99.9999%", professional: "99.999%", standard: "99.99%" },
    { metric: "Response Time", enterprise: "< 1 hour", professional: "< 4 hours", standard: "< 8 hours" },
    { metric: "Resolution Time", enterprise: "< 4 hours", professional: "< 24 hours", standard: "< 48 hours" },
    { metric: "Communication", enterprise: "Real-time", professional: "Hourly", standard: "Daily" },
    { metric: "Credits (Breach)", enterprise: "30% monthly", professional: "20% monthly", standard: "10% monthly" },
  ],
  monitoring: [
    "All SLA metrics are monitored in real-time through our internal dashboards",
    "Clients receive automated SLA compliance reports monthly",
    "Independent audits verify SLA adherence quarterly",
  ],
  guarantees: [
    "Uptime measured across all core services",
    "Response time measured from incident detection to first client communication",
    "Resolution time measured from incident detection to full service restoration",
    "Service credits applied automatically upon SLA breach",
  ],
  cta: [
    { label: "Review SLA Terms", href: "/contact" },
    { label: "Contact Sales", href: "#communication-channels" },
  ],
};

export interface MonitoringData {
  eyebrow: string;
  title: string;
  intro: string;
  stack: { layer: string; tools: string; purpose: string }[];
  coverage: string[];
  alerts: { level: string; trigger: string; response: string }[];
  failover: { title: string; body: string };
  cta: { label: string; href: string }[];
  pillars?: { title: string; body: string }[];
}

export const monitoringInfrastructure: MonitoringData = {
  eyebrow: "Monitoring Infrastructure",
  title: "How We Monitor",
  intro: "Our monitoring infrastructure provides comprehensive visibility into platform health across all services, regions, and dependencies.",
  stack: [
    { layer: "Synthetic Monitoring", tools: "Custom probes", purpose: "Health checks every 10 seconds" },
    { layer: "Real User Monitoring", tools: "Performance tracking", purpose: "Client experience metrics" },
    { layer: "Infrastructure Monitoring", tools: "CPU, memory, disk, network", purpose: "Resource utilization" },
    { layer: "Application Monitoring", tools: "APM tools", purpose: "Transaction tracing" },
    { layer: "Security Monitoring", tools: "SIEM systems", purpose: "Threat detection" },
  ],
  coverage: [
    "2,000+ health endpoints monitored continuously",
    "10-second check intervals for critical services",
    "30-second check intervals for standard services",
    "60-second check intervals for non-critical services",
    "Multi-region redundancy for monitoring infrastructure",
  ],
  alerts: [
    { level: "Critical", trigger: "Service outage", response: "Immediate team notification" },
    { level: "Warning", trigger: "Performance degradation", response: "Investigation within 5 minutes" },
    { level: "Informational", trigger: "Anomaly detected", response: "Review within 1 hour" },
  ],
  failover: {
    title: "Failover Capabilities",
    body: "Automatic failover activates within 500 milliseconds of service degradation detection. Redundant systems across multiple regions ensure continuous availability even during infrastructure events.",
  },
  cta: [{ label: "View Monitoring Architecture", href: "#monitoring-infrastructure" }],
};

export interface ResponseData {
  eyebrow: string;
  title: string;
  intro: string;
  process: { phase: string; action: string; timeline: string }[];
  communication: string[];
  escalation: { level: string; trigger: string; action: string }[];
  cta: { label: string; href: string }[];
  pillars?: { title: string; body: string }[];
}

export const responseProtocols: ResponseData = {
  eyebrow: "Response Protocols",
  title: "Incident Response Framework",
  intro: "When incidents occur, our response protocols activate immediately. Every incident follows a structured process designed to minimize impact and restore service as quickly as possible.",
  process: [
    { phase: "Detection", action: "Automated monitoring detects anomaly", timeline: "Immediate" },
    { phase: "Triage", action: "Severity assessment and team assignment", timeline: "< 5 minutes" },
    { phase: "Communication", action: "Status page updated, stakeholders notified", timeline: "< 10 minutes" },
    { phase: "Investigation", action: "Root cause analysis begins", timeline: "< 15 minutes" },
    { phase: "Resolution", action: "Fix deployed, service restored", timeline: "Varies by severity" },
    { phase: "Post-Mortem", action: "Root cause analysis completed", timeline: "< 48 hours" },
  ],
  communication: [
    "Status page updated within 10 minutes of detection",
    "Email notifications sent to affected clients within 15 minutes",
    "Real-time updates every 5 minutes for Critical incidents",
    "Real-time updates every 15 minutes for Major incidents",
    "Post-incident summary published within 24 hours",
  ],
  escalation: [
    { level: "Level 1", trigger: "Initial detection", action: "On-call engineer assigned" },
    { level: "Level 2", trigger: "No resolution in 15 minutes", action: "Senior engineer escalated" },
    { level: "Level 3", trigger: "No resolution in 30 minutes", action: "Engineering leadership engaged" },
    { level: "Level 4", trigger: "No resolution in 1 hour", action: "Executive notification" },
  ],
  cta: [{ label: "Review Response Protocols", href: "#response-protocols" }],
};

export interface GlobalData {
  eyebrow: string;
  title: string;
  intro: string;
  regions: { region: string; infra: string; connectivity: string; status: string }[];
  performance: { region: string; avg: string; p99: string; availability: string }[];
  failover: { title: string; body: string };
  dataSovereignty: { title: string; body: string };
  cta: { label: string; href: string }[];
  pillars?: { title: string; body: string }[];
}

export const globalOps: GlobalData = {
  eyebrow: "Global Operations Status",
  title: "Multi-Continental Infrastructure",
  intro: "Perception X2 operates across multiple continents with redundant infrastructure in each region. Regional status reflects the health of local infrastructure and connectivity.",
  regions: [
    { region: "North America", infra: "Operational", connectivity: "Operational", status: "Operational" },
    { region: "Europe", infra: "Operational", connectivity: "Operational", status: "Operational" },
    { region: "Asia-Pacific", infra: "Operational", connectivity: "Operational", status: "Operational" },
  ],
  performance: [
    { region: "North America", avg: "23ms", p99: "45ms", availability: "99.9999%" },
    { region: "Europe", avg: "31ms", p99: "58ms", availability: "99.9999%" },
    { region: "Asia-Pacific", avg: "42ms", p99: "79ms", availability: "99.9999%" },
  ],
  failover: {
    title: "Failover Capabilities",
    body: "Each region maintains independent failover capabilities. If primary infrastructure in any region experiences degradation, traffic automatically routes to the nearest healthy region within 500 milliseconds.",
  },
  dataSovereignty: {
    title: "Data Sovereignty",
    body: "All data processing respects regional data sovereignty requirements. Data remains within its originating region unless explicitly configured for cross-region processing by the client.",
  },
  cta: [{ label: "View Regional Details", href: "#global-operations-status" }],
};

export interface SecurityData {
  eyebrow: string;
  title: string;
  intro: string;
  posture: { category: string; status: string; audit: string }[];
  metrics: { metric: string; value: string; status: string }[];
  infrastructure: string[];
  cta: { label: string; href: string }[];
  pillars?: { title: string; body: string }[];
}

export const securityStatus: SecurityData = {
  eyebrow: "Security Status",
  title: "Zero Security Incidents. Verified.",
  intro: "Security is foundational to Perception X2. We maintain zero security incidents across 15+ years of operations through military-grade security architecture and continuous threat monitoring.",
  posture: [
    { category: "SOC 2 Type II", status: "Compliant", audit: "2026-03-15" },
    { category: "ISO 27001", status: "Certified", audit: "2026-02-28" },
    { category: "Penetration Testing", status: "Passed", audit: "2026-04-10" },
    { category: "Vulnerability Scanning", status: "Clear", audit: "Continuous" },
    { category: "Threat Monitoring", status: "Active", audit: "24/7/365" },
  ],
  metrics: [
    { metric: "Security Incidents", value: "0", status: "Verified" },
    { metric: "Mean Time to Detect", value: "< 1 minute", status: "Exceeds" },
    { metric: "Mean Time to Respond", value: "< 5 minutes", status: "Exceeds" },
    { metric: "Vulnerability Patch Time", value: "< 24 hours", status: "Exceeds" },
    { metric: "Encryption Standard", value: "AES-256", status: "Active" },
  ],
  infrastructure: [
    "End-to-end AES-256 encryption for all data in transit and at rest",
    "TLS 1.3 for all API communications",
    "Zero-trust architecture across all services",
    "Continuous intrusion detection and prevention",
    "Regular third-party security audits",
  ],
  cta: [
    { label: "Review Security Architecture", href: "/about#technology" },
    { label: "View Security Whitepaper", href: "#communication-channels" },
  ],
};

export interface UpdatesData {
  eyebrow: string;
  title: string;
  intro: string;
  recent: { date: string; update: string; impact: string; status: string }[];
  categories: { category: string; frequency: string; impact: string }[];
  communication: string[];
  cta: { label: string; href: string }[];
  pillars?: { title: string; body: string }[];
}

export const platformUpdates: UpdatesData = {
  eyebrow: "Platform Updates",
  title: "Recent Improvements",
  intro: "Perception X2 is continuously evolving. Recent platform updates include performance improvements, new features, and security enhancements.",
  recent: [
    { date: "2026-06-10", update: "API v3.2 Release", impact: "Enhanced query performance", status: "Deployed" },
    { date: "2026-06-05", update: "AI Engine Optimization", impact: "15% faster processing", status: "Deployed" },
    { date: "2026-05-28", update: "Security Patch", impact: "Vulnerability remediation", status: "Deployed" },
    { date: "2026-05-20", update: "Dashboard Enhancement", impact: "Improved analytics views", status: "Deployed" },
    { date: "2026-05-12", update: "CDN Optimization", impact: "20% faster content delivery", status: "Deployed" },
  ],
  categories: [
    { category: "Security Patches", frequency: "As needed", impact: "Critical" },
    { category: "Performance Optimizations", frequency: "Bi-weekly", impact: "High" },
    { category: "Feature Releases", frequency: "Monthly", impact: "Medium" },
    { category: "Infrastructure Updates", frequency: "Quarterly", impact: "Low" },
  ],
  communication: [
    "Release notes published 48 hours before deployment",
    "Email notifications sent to affected clients",
    "Status page updated during deployment",
    "Post-deployment verification completed",
  ],
  cta: [
    { label: "View Release Notes", href: "#platform-updates" },
    { label: "Subscribe to Updates", href: "#communication-channels" },
  ],
};

export interface CommChannelsData {
  eyebrow: string;
  title: string;
  intro: string;
  primary: { channel: string; purpose: string; frequency: string }[];
  subscriptions: { type: string; email: string; rss: string; webhook: string }[];
  contacts: { purpose: string; channel: string; response: string }[];
  cta: { label: string; href: string }[];
  pillars?: { title: string; body: string }[];
}

export const communicationChannels: CommChannelsData = {
  eyebrow: "Communication Channels",
  title: "Stay Informed",
  intro: "Multiple channels ensure you receive platform status updates through your preferred method.",
  primary: [
    { channel: "Status Page", purpose: "Real-time status", frequency: "Continuous" },
    { channel: "Email Notifications", purpose: "Incident alerts", frequency: "As needed" },
    { channel: "RSS Feed", purpose: "Status updates", frequency: "Real-time" },
    { channel: "Webhook Integration", purpose: "Automated status", frequency: "Real-time" },
  ],
  subscriptions: [
    { type: "All Incidents", email: "Yes", rss: "Yes", webhook: "Yes" },
    { type: "Critical Only", email: "Yes", rss: "Yes", webhook: "Yes" },
    { type: "Scheduled Maintenance", email: "Yes", rss: "Yes", webhook: "Yes" },
    { type: "Performance Reports", email: "Yes", rss: "No", webhook: "No" },
  ],
  contacts: [
    { purpose: "Urgent Issues", channel: "Status page + email", response: "< 15 minutes" },
    { purpose: "General Inquiries", channel: "Email", response: "< 4 hours" },
    { purpose: "SLA Questions", channel: "Email", response: "< 8 hours" },
    { purpose: "Security Concerns", channel: "Security team", response: "< 1 hour" },
  ],
  cta: [
    { label: "Subscribe to Notifications", href: "#communication-channels" },
    { label: "Contact Support", href: "#communication-channels" },
  ],
};

export interface StatusApiData {
  eyebrow: string;
  title: string;
  intro: string;
  endpoints: { endpoint: string; method: string; purpose: string }[];
  responseFormat: string;
  rateLimits: { tier: string; rpm: string; burst: string }[];
  auth: { title: string; body: string };
  cta: { label: string; href: string }[];
  pillars?: { title: string; body: string }[];
}

export const statusApi: StatusApiData = {
  eyebrow: "Status API",
  title: "Programmatic Access",
  intro: "Integrate Perception X2 status data into your monitoring systems through our Status API.",
  endpoints: [
    { endpoint: "/api/v1/status", method: "GET", purpose: "Current platform status" },
    { endpoint: "/api/v1/status/history", method: "GET", purpose: "Historical status data" },
    { endpoint: "/api/v1/incidents", method: "GET", purpose: "Incident history" },
    { endpoint: "/api/v1/maintenance", method: "GET", purpose: "Scheduled maintenance" },
    { endpoint: "/api/v1/metrics", method: "GET", purpose: "Performance metrics" },
  ],
  responseFormat: `{
  "status": "operational",
  "timestamp": "2026-06-14T12:00:00Z",
  "services": {
    "platform": "operational",
    "api": "operational",
    "engines": "operational",
    "infrastructure": "operational"
  },
  "uptime": "99.9999%",
  "last_incident": null
}`,
  rateLimits: [
    { tier: "Standard", rpm: "60", burst: "10" },
    { tier: "Enterprise", rpm: "600", burst: "100" },
  ],
  auth: {
    title: "Authentication",
    body: "API access requires an API key. Contact your account manager or support team to obtain credentials.",
  },
  cta: [
    { label: "View API Documentation", href: "#status-api" },
    { label: "Request API Access", href: "#communication-channels" },
  ],
};

export interface HistoryData {
  eyebrow: string;
  title: string;
  intro: string;
  quarterly: { quarter: string; uptime: string; avg: string; incidents: string }[];
  yearly: { year: string; uptime: string; avg: string; incidents: string }[];
  trend: { title: string; body: string };
  sla: { period: string; target: string; actual: string; status: string }[];
  audit: string;
  pillars?: { title: string; body: string }[];
}

export const historicalPerformance: HistoryData = {
  eyebrow: "Historical Performance",
  title: "Long-Term Metrics",
  intro: "Historical performance data demonstrates the consistency and reliability of Perception X2 infrastructure.",
  quarterly: [
    { quarter: "Q2 2026", uptime: "100.0000%", avg: "38ms", incidents: "0" },
    { quarter: "Q1 2026", uptime: "99.9999%", avg: "41ms", incidents: "0" },
    { quarter: "Q4 2025", uptime: "99.9999%", avg: "43ms", incidents: "0" },
    { quarter: "Q3 2025", uptime: "99.9999%", avg: "45ms", incidents: "0" },
    { quarter: "Q2 2025", uptime: "99.9999%", avg: "47ms", incidents: "0" },
    { quarter: "Q1 2025", uptime: "99.9999%", avg: "49ms", incidents: "0" },
  ],
  yearly: [
    { year: "2026 (YTD)", uptime: "99.9999%", avg: "39ms", incidents: "0" },
    { year: "2025", uptime: "99.9999%", avg: "46ms", incidents: "3" },
    { year: "2024", uptime: "99.9999%", avg: "52ms", incidents: "4" },
  ],
  trend: {
    title: "Trend Analysis",
    body: "Platform performance has improved consistently year-over-year. Response times have decreased 25% since 2024, while uptime has remained at 99.9999% or higher across all periods.",
  },
  sla: [
    { period: "2026 YTD", target: "99.9999%", actual: "99.9999%", status: "Compliant" },
    { period: "2025", target: "99.9999%", actual: "99.9999%", status: "Compliant" },
    { period: "2024", target: "99.9999%", actual: "99.9999%", status: "Compliant" },
  ],
  audit: "Historical data audited quarterly by independent monitoring services",
};

export interface CtaData {
  eyebrow: string;
  title: string;
  intro: string;
  support: string[];
  documentation: string[];
  demo: { title: string; body: string };
  cta: { label: string; href: string }[];
  pillars?: { title: string; body: string }[];
}

export const statusCta: CtaData = {
  eyebrow: "Get Started",
  title: "Need Assistance?",
  intro: "Our support team is available 24/7/365 for active clients. For urgent issues, use the contact options below for immediate assistance.",
  support: [
    "Status Page — Real-time platform status and incident updates",
    "Email Support — support@perception.ac for non-urgent inquiries",
    "Emergency Contact — Available through client portal for critical issues",
    "API Status — Programmatic access for integration into your monitoring systems",
  ],
  documentation: [
    "Platform documentation available at perception.ac/developers",
    "API reference at perception.ac/developers/api-reference",
    "Security whitepaper available upon request",
  ],
  demo: {
    title: "Request a Demo",
    body: "Experience Perception X2 firsthand. Our team will demonstrate platform capabilities and discuss how we can support your operational requirements.",
  },
  cta: [
    { label: "Contact Support", href: "/support" },
    { label: "Request a Demo", href: "/demo" },
    { label: "View Documentation", href: "/support" },
  ],
};
